import { useTranslation } from 'react-i18next'
import { Button } from '@structural-climbing/ui'
import { ageFrom } from './ageFrom'

export interface ProfileDetailItem {
  label: string
  value: string | null | undefined
}

const DATE_FORMAT: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' }

/** A timestamp as a date, in the viewer's zone. */
export function formatDetailDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, DATE_FORMAT)
}

/**
 * A plain `YYYY-MM-DD`, shown as that same calendar day. `new Date('1990-05-04')`
 * is UTC midnight, which is the 3rd for anyone west of Greenwich.
 */
export function formatDetailDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString(undefined, DATE_FORMAT)
}

/** The rows both profile modals share, so they read the same for a member and a child. */
export function usePersonDetailItems() {
  const { t, i18n } = useTranslation()

  return {
    birth(day: string | null | undefined): ProfileDetailItem {
      return {
        label: t('members.detailsDateOfBirth'),
        value: day
          ? `${formatDetailDay(day)} · ${t('members.childAge', { count: ageFrom(day) })}`
          : null,
      }
    },
    gender(gender: string | null | undefined): ProfileDetailItem {
      const labels: Record<string, string> = {
        male: t('members.genderMale'),
        female: t('members.genderFemale'),
        prefer_not_to_say: t('members.genderPreferNotToSay'),
      }
      return { label: t('members.detailsGender'), value: gender ? (labels[gender] ?? null) : null }
    },
    language(code: string | null | undefined): ProfileDetailItem {
      let name: string | null = null
      if (code) {
        try {
          name = new Intl.DisplayNames([i18n.language], { type: 'language' }).of(code) ?? code
        } catch {
          name = code
        }
      }
      return { label: t('members.detailsLanguage'), value: name }
    },
  }
}

/**
 * Label and value pairs about a person. Rows with nothing on record are left out.
 * With `onEdit` the heading carries a pencil, and the section shows even when
 * every row is empty, since that is exactly when someone needs to fill it in.
 */
export function ProfileDetails({
  items,
  onEdit,
}: {
  items: ProfileDetailItem[]
  onEdit?: () => void
}) {
  const { t } = useTranslation()
  const shown = items.filter((i) => i.value)
  if (shown.length === 0 && !onEdit) return null

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
          {t('members.detailsTitle')}
        </p>
        {onEdit && (
          <Button
            isIconOnly
            size="sm"
            variant="light"
            className="-my-1.5"
            onClick={onEdit}
            aria-label={t('members.editDetails')}
            title={t('members.editDetails')}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"
              />
            </svg>
          </Button>
        )}
      </div>
      {shown.length > 0 && (
        <dl className="grid gap-x-4 gap-y-2 rounded-lg bg-content2/50 px-3 py-2 sm:grid-cols-2">
          {shown.map((item) => (
            <div key={item.label} className="min-w-0">
              <dt className="text-xs text-foreground/50">{item.label}</dt>
              <dd className="truncate text-sm">{item.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}
